/**
 * CloudEmergencyTable — real-time emergency alerts table for Cloud 5.0 platform.
 * Source: activeCloudAlarms from NotificationContext.
 *
 * Columns: Event Start, Duration, Category/Type, Device/Serial, Description,
 *          Area, Resident, Accepted By/Time, Device Cleared, Status
 *
 * Schema uses snake_case fields (event_id, event_datetime_utc, event_category, etc.)
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow, durationBetween } from '../../../../libs.jsx';
import AlertStatusBadge from './AlertStatusBadge.jsx';
import AlertTypeBadge from './AlertTypeBadge.jsx';
import BlockedReasonTip from '../../../../components/v2/BlockedReasonTip.jsx';
import { alertActionState } from '../../../../utils/alertActions.js';
import { getAlertDeviceLink } from '../../../../utils/deviceLinks.js';
import { getRoleWeight, getCampusTzOffset } from '../../../../services/session.js';
import { formatUtcForCampus } from '../../../../utils/campusTime.js';
import SortableTh from '../../../../components/v2/SortableTh.jsx';
import InfoPopover from '../../../../components/v2/InfoPopover.jsx';

/* Weight required to open a server profile — mirrors Community.jsx's own gate. */

/** Hook that ticks every second to force duration re-renders */
function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const OVERDUE_MS = 5 * 60 * 1000;
const CRITICAL_MS = 10 * 60 * 1000;

const getDurationColor = (status, eventStart) => {
    if (status !== 'Overdue' || !eventStart) return undefined;
    const elapsed = Date.now() - new Date(eventStart.endsWith?.('Z') ? eventStart : eventStart + 'Z').getTime();
    if (elapsed > CRITICAL_MS) return 'var(--color-danger, #e63757)';
    if (elapsed > OVERDUE_MS) return 'var(--color-warning-text, #d97706)';
    return undefined;
};

/** Duration cell — green when cleared, yellow/red when overdue; hover, focus or tap shows the response time */
const DurationCell = ({ eventStart, ackTime, status }) => {
    const hasAck = Boolean(ackTime);

    const color = hasAck
        ? 'var(--color-success-text, #0cce6b)'
        : status === 'Accepted'
            ? 'var(--color-primary, #0081C7)'
            : getDurationColor(status, eventStart);

    const duration = (
        <span className={`na-duration${hasAck ? ' na-duration--has-popover' : ''}`} style={color ? { color } : undefined}>
            {durationToNow(eventStart)}
        </span>
    );
    if (!hasAck) return duration;

    return (
        <InfoPopover marker="none" heading="Response Time" body={durationBetween(eventStart, ackTime)} maxWidth={260}>
            {duration}
        </InfoPopover>
    );
};

const CloudEmergencyTable = ({
    data, sortColumn, sortOrder, onSort,
    canInteract = false, pendingEventId = null, onAccept, onRelease, onFinish, onAcknowledge, onViewVideo,
    acceptBlockedReason = () => null,
    ackIntegrationActive = false, canViewVideo = false,
    highPriorityTypeIds = new Set(),
    sessionUserId = null, sessionUsername = null,
    /* Campus time for the UTC timestamps; the current campus by default. */
    tzOffset = getCampusTzOffset(),
}) => {
    useTick(1000); // Re-render every second for live duration updates

    // Actions column renders when portal interactions are on (Accept/Finish) OR an
    // acknowledge integration is active for this campus (e.g. SafelyYou \u2192 the
    // Acknowledge link button below), so it shows even when portal_afr is off.
    const showActions = Boolean(canInteract) || Boolean(ackIntegrationActive);
    const colSpan = showActions ? 12 : 11;

    /** Row-level action buttons.
     *  Accept / Finish are portal actions (gated on canInteract = portal_afr).
     *  Acknowledge is the SafelyYou action (gated on ackIntegrationActive), for
     *  event_type_id 40 alerts that carry an acknowledge_link and are pre-clear:
     *   - Acknowledge (POST) \u2014 must be accepted first (unless accept isn't required)
     *   - View Video (GET link, external) \u2014 role_weight > 10 only, any accept state */
    const renderRowActions = (row) => {
        /* Eligibility is shared with the mobile card (utils/alertActions.js) so the
           two views cannot disagree. Finish and Release are accepter-only; portal
           accept/finish/release additionally require portal_afr (canInteract). */
        const act = alertActionState(row, { sessionUserId, sessionUsername });

        const showAccept = canInteract && act.acceptable;
        const showFinish = canInteract && act.finishable;
        /* Release — hand an accepted alert back so someone else can take it. A
           caregiver can give up an alert they cannot attend, but nobody can take
           one out from under an active response. */
        const showRelease = canInteract && act.releasable;

        /* Non-null when this user is already holding a different alert. Accept stays
           rendered — disabled, with the reason on hover — rather than vanishing: a
           button that silently disappears reads as a bug, and the reason ("finish or
           release the one you have") is the whole point of the rule. */
        const acceptBlocked = showAccept ? acceptBlockedReason(row) : null;

        // SafelyYou acknowledge (pre-clear, has link). Acknowledge is the accepter's
        // call when acceptance was required; View Video (the GET link) stays open to
        // role_weight > 10 regardless of accept state.
        const ackEligible = ackIntegrationActive && act.ackCandidate;
        const showAcknowledge = ackIntegrationActive && act.acknowledgeable;
        const showViewVideo = ackEligible && canViewVideo;

        const pending = pendingEventId === row.event_id;

        if (!showAccept && !showRelease && !showFinish && !showAcknowledge && !showViewVideo) {
            return <span className="na-muted">{'\u2014'}</span>;
        }

        return (
            <div className="na-row-actions">
                {showAccept ? (
                    <BlockedReasonTip message={acceptBlocked || ''}>
                        <button
                            type="button"
                            className="na-action-btn na-action-btn--accept"
                            onClick={() => onAccept && onAccept(row)}
                            disabled={pending || !onAccept || Boolean(acceptBlocked)}
                            title={acceptBlocked ? undefined : 'Accept this alert'}
                        >
                            {pending ? 'Accepting\u2026' : 'Accept'}
                        </button>
                    </BlockedReasonTip>
                ) : null}
                {showRelease ? (
                    <button
                        type="button"
                        className="na-action-btn na-action-btn--release"
                        onClick={() => onRelease && onRelease(row)}
                        disabled={pending || !onRelease}
                        title="Release this alert so another caregiver can accept it"
                    >
                        {pending ? 'Releasing\u2026' : 'Release'}
                    </button>
                ) : null}
                {showFinish ? (
                    <button
                        type="button"
                        className="na-action-btn na-action-btn--finish"
                        onClick={() => onFinish && onFinish(row)}
                        disabled={pending || !onFinish}
                        title="Finish this alert"
                    >
                        {pending ? 'Finishing\u2026' : 'Finish'}
                    </button>
                ) : null}
                {showAcknowledge ? (
                    <button
                        type="button"
                        className="na-action-btn na-action-btn--acknowledge"
                        onClick={() => onAcknowledge && onAcknowledge(row)}
                        disabled={pending || !onAcknowledge}
                        title="Acknowledge this fall alert"
                    >
                        {pending ? 'Acknowledging…' : 'Acknowledge'}
                    </button>
                ) : null}
                {showViewVideo ? (
                    <a
                        className="na-action-btn na-action-btn--view-video"
                        href={row.acknowledge_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open SafelyYou to view the video (opens in a new tab)"
                        onClick={() => onViewVideo && onViewVideo(row)}
                    >
                        View Video
                        <i className="fe fe-external-link" aria-hidden="true" />
                    </a>
                ) : null}
            </div>
        );
    };

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    /* Device-name link, shared with the Maintenance and Lost Devices tables:
       server-offline (type 41) goes to the server profile at weight 80+, every
       other row follows the device-profile rule (utils/deviceLinks.js). */
    const getDeviceLink = (row) => getAlertDeviceLink(row, getRoleWeight());

    const getBorderColor = (category) => {
        const cat = (category || '').toLowerCase();
        if (cat === 'emergency') return 'var(--color-danger)';
        if (cat === 'maintenance') return 'var(--color-warning)';
        return 'var(--color-primary)';
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <SortableTh sortKey="event_datetime_utc" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Event Start{sortIcon('event_datetime_utc')}</SortableTh>
                        <th className="dp-th">Duration</th>
                        <SortableTh sortKey="event_category" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Category / Type{sortIcon('event_category')}</SortableTh>
                        <th className="dp-th">Device / Serial</th>
                        <SortableTh sortKey="device_description" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Description{sortIcon('device_description')}</SortableTh>
                        <SortableTh sortKey="area_name" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Area{sortIcon('area_name')}</SortableTh>
                        <SortableTh sortKey="near_area_name" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Near Area{sortIcon('near_area_name')}</SortableTh>
                        <th className="dp-th">Resident</th>
                        <SortableTh sortKey="accepted_display" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Accepted By / Time{sortIcon('accepted_display')}</SortableTh>
                        <SortableTh sortKey="ack_datetime_utc" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Device Cleared{sortIcon('ack_datetime_utc')}</SortableTh>
                        <SortableTh sortKey="status" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Status{sortIcon('status')}</SortableTh>
                        {showActions ? <th className="dp-th">Actions</th> : null}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={colSpan}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">{'\u2713'}</div>
                                    <div className="na-empty__text">No active emergency alerts</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => {
                        const deviceLink = getDeviceLink(row);
                        const isHighPriority = highPriorityTypeIds.has((row.event_type_id ?? '').toString());
                        return (
                            <tr
                                key={row.event_id}
                                className={`dp-tr na-row-border${isHighPriority ? ' na-row--priority' : ''}`}
                                style={{ borderLeftColor: isHighPriority ? 'var(--color-danger, #e63757)' : getBorderColor(row.event_category) }}
                            >
                                <td className="dp-td" style={{ whiteSpace: 'break-spaces' }}>{formatUtcForCampus(row.event_datetime_utc, tzOffset)}</td>
                                <td className="dp-td">
                                    <DurationCell eventStart={row.event_datetime_utc} ackTime={row.ack_datetime_utc} status={row.status} />
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {isHighPriority ? (
                                            <span className="na-badge na-badge--priority" title="High priority event type">
                                                {'⚑'} High Priority
                                            </span>
                                        ) : null}
                                        <AlertTypeBadge alertType={row.event_category} alertSubType={row.event_type} />
                                    </div>
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {deviceLink ? (
                                            <Link to={deviceLink} className="ar-link">{row.device_name || '\u2014'}</Link>
                                        ) : (<span>{row.device_name || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.manufacture_uuid || ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td" style={{ whiteSpace: 'break-spaces' }}>{row.device_description || '\u2014'}</td>
                                <td className="dp-td">
                                    {row.area_id ? (
                                        <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{row.area_name || '\u2014'}</Link>
                                    ) : (row.area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {row.near_area_id ? (
                                        <Link to={`/profiles/area/${row.near_area_id}`} className="ar-link">{row.near_area_name || '\u2014'}</Link>
                                    ) : (row.near_area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {row.residents?.length ? (
                                        <div className="na-stack">
                                            {row.residents.map((r, i) => (
                                                r.resident_id ? (
                                                    <Link key={r.resident_id} to={`/profiles/resident/${r.resident_id}`} className="ar-link">{r.resident_name || '\u2014'}</Link>
                                                ) : (<span key={`r-${i}`}>{r.resident_name || '\u2014'}</span>)
                                            ))}
                                        </div>
                                    ) : '\u2014'}
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {row.accepted_user_id ? (
                                            <Link to={`/profiles/user/${row.accepted_user_id}`} className="ar-link">{row.accepted_display || '\u2014'}</Link>
                                        ) : (<span>{row.accepted_display || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.accepted_datetime_utc ? formatUtcForCampus(row.accepted_datetime_utc, tzOffset) : ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td" style={{ whiteSpace: 'break-spaces' }}>{formatUtcForCampus(row.ack_datetime_utc, tzOffset)}</td>
                                <td className="dp-td"><AlertStatusBadge status={row.status} eventDateTime={row.event_datetime_utc} /></td>
                                {showActions ? (
                                    <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>
                                        {renderRowActions(row)}
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default CloudEmergencyTable;
